/**
 * AI:
 * Status: 'generated'
 * Type: Component
 * SubType: Configuration / Read-only Overview
 * Reason: Third, read-only view of the bd.DunningType OBC. It presents the deadline
 *         and level configuration in business-readable form: start conditions
 *         (paths A/B/C), the dunning level schedule with the four resolved
 *         Model-C offsets including their provenance (configured vs. system
 *         default), the spelled-out deadline formulas, an example date chain for
 *         a selectable reference date, the section 193 BGB roll, transitions,
 *         actions, charges, stop conditions, special handlings and the extension
 *         points.
 *
 *         Deliberately a hand-built panel and NOT a generic WidgetFactory table
 *         widget (capability matrix row H3, signed): the schedule interleaves
 *         level x offsets x example data x actions into a narrative that a
 *         generic table cannot express. Precedent in the same module: the
 *         hand-built overview tab in dunning-object.component.html.
 *
 *         NO date arithmetic happens in the frontend: bank days, holidays and the
 *         section 193 roll come exclusively from the backend
 *         (BdDunningDeadlineService / BankCalendarService) via
 *         DunningTypeService.getDeadlineDerivation (foundational decision 11.1).
 *
 *         The start conditions are grouped per billing area and rendered as an
 *         accordion: on a productive tenant the flat list runs over dozens of
 *         rows and is no longer usable. Grouping happens once per structure
 *         load in the component, never in the template.
 *
 *         Read-only: no edit buttons, no dialogs, no save paths.
 *
 * @ticket bd-dunningtype-overview
 */
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {forkJoin, Subscription} from 'rxjs';

import {DunningTypeDto} from '../../../../../model/dto/entity/dunning-type.dto';
import {
    BdDunningTypeCalendarInfoDto,
    BdDunningTypeDeadlineDerivationDto,
    BdDunningTypeLevelDerivationDto,
    BdDunningTypeResolvedOffsetDto
} from '../../../../../model/dto/api/bd-dunning-type-deadline-derivation.dto';
import {
    BdDunningTypeChargeDto,
    BdDunningTypeExitDto,
    BdDunningTypeLevelActionDto,
    BdDunningTypeLevelDto,
    BdDunningTypeLevelSpecialHandlingDto,
    BdDunningTypeLevelTransitionDto,
    BdDunningTypeStartDto,
    BdDunningTypeStopDto
} from '../../../../../model/dto/api/bd-dunning-type-structure.dto';
import {
    BdDunningTypeStartGroup,
    START_GROUP_KEY_ALL
} from '../../../../../interface/bd-dunning-type-start-group.interface';
import {BdDunningTypeStructureService} from '../../../../../service/api/bd-dunning-type-structure.service';
import {DunningTypeService} from '../../../../../service/api/dunning-type.service';
import {formatIsoDurationDe} from '../../../../../util/bd-iso-duration.util';

import {
    BD_DUNNING_OFFSET_SOURCE_LABELS,
    BdDunningOffsetSource
} from '../../../../../enum/bd-dunning-offset-source.enum';
import {
    DUNNING_DAY_COUNT_TYPE_LABELS,
    DUNNING_DAY_COUNT_TYPE_SHORT_LABELS,
    DunningDayCountType
} from '../../../../../enum/dunning-day-count-type.enum';
import {
    DunningDeadlineRollMode
} from '../../../../../enum/dunning-deadline-roll-mode.enum';
import {BdDunningActionType} from '../../../../../enum/bd-dunning-action-type.enum';
import {BdDunningActionExecutionType} from '../../../../../enum/bd-dunning-action-execution-type.enum';
import {
    BD_DUNNING_ACTION_TIMING_LABELS,
    BdDunningActionTiming
} from '../../../../../enum/bd-dunning-action-timing.enum';
import {
    BdDunningChargeValutaDateTiming
} from '../../../../../enum/bd-dunning-charge-valuta-date-timing.enum';
import {
    BD_DUNNING_TRANSITION_TYPE_LABELS,
    BdDunningTransitionType
} from '../../../../../enum/bd-dunning-transition-type.enum';
import {
    BD_DUNNING_BALANCE_CHECK_LABELS,
    BdDunningBalanceCheck
} from '../../../../../enum/bd-dunning-balance-check.enum';
import {
    DUNNING_STOP_CONDITION_TYPE_LABELS,
    DunningStopConditionType
} from '../../../../../enum/dunning-stop-condition-type.enum';
import {
    DUNNING_BALANCE_FIELD_LABELS,
    DunningBalanceField
} from '../../../../../enum/dunning-balance-field.enum';
import {
    BD_DUNNING_STOP_WAITING_PERIOD_TYPE_LABELS,
    BdDunningStopWaitingPeriodType
} from '../../../../../enum/bd-dunning-stop-waiting-period-type.enum';
import {
    DUNNING_SPECIAL_HANDLING_TYPE_LABELS,
    DunningSpecialHandlingType
} from '../../../../../enum/dunning-special-handling-type.enum';
import {
    BD_DUNNING_EXIT_POINT_LABELS,
    BdDunningExitPoint
} from '../../../../../enum/bd-dunning-exit-point.enum';
import {
    BD_DUNNING_EXIT_EXECUTION_TYPE_LABELS,
    BdDunningExitExecutionType
} from '../../../../../enum/bd-dunning-exit-execution-type.enum';
import {
    paymentMethodLabel as resolvePaymentMethodLabel,
    PaymentMethod
} from '../../../../../enum/payment-method.enum';
import {
    BdDunningExitUiComponent
} from '../../../../../enum/bd-dunning-exit-ui-component.enum';
import {
    BdDunningDistributionAllocationOrderEnum
} from '../../../../../enum/bd-dunning-distribution-allocation-order.enum';
import {EntityStatusEnum} from '../../../../../../../core/shared/entity/enum/entity-status.enum';

/**
 * German UI labels for BdDunningActionType.
 *
 * Defined locally because the data layer's enum mirror for this one ships no label
 * record (unlike the newer mirrors) and is not modified as part of this work.
 */
const ACTION_TYPE_LABELS: Record<BdDunningActionType, string> = {
    [BdDunningActionType.NOTIFICATION]: 'Benachrichtigung',
    [BdDunningActionType.AGENT_ACTIVITY]: 'Agenten-Aktivität',
    [BdDunningActionType.CHARGE]: 'Gebühr',
    [BdDunningActionType.TICKET]: 'Ticket',
    [BdDunningActionType.PAYMENT_METHOD_SWITCH]: 'Zahlartwechsel'
};

/** German UI labels for BdDunningActionExecutionType (see ACTION_TYPE_LABELS). */
const ACTION_EXECUTION_TYPE_LABELS: Record<BdDunningActionExecutionType, string> = {
    [BdDunningActionExecutionType.AUTOMATIC]: 'Automatisch',
    [BdDunningActionExecutionType.MANUAL]: 'Manuell'
};

/** German UI labels for BdDunningChargeValutaDateTiming (see ACTION_TYPE_LABELS). */
const CHARGE_VALUTA_DATE_TIMING_LABELS: Record<BdDunningChargeValutaDateTiming, string> = {
    [BdDunningChargeValutaDateTiming.DUNNING_START]: 'Mahnungsbeginn',
    [BdDunningChargeValutaDateTiming.DUNNING_STEP_START]: 'Stufenbeginn',
    [BdDunningChargeValutaDateTiming.TODAY]: 'Heute'
};

/** German UI labels for the payment distribution allocation order. */
const ALLOCATION_ORDER_LABELS: Record<BdDunningDistributionAllocationOrderEnum, string> = {
    [BdDunningDistributionAllocationOrderEnum.OLDEST_FIRST]: 'Älteste Forderungen zuerst',
    [BdDunningDistributionAllocationOrderEnum.DUNNING_LEVEL]: 'Höchste Mahnstufe zuerst',
    [BdDunningDistributionAllocationOrderEnum.CHARGES_FIRST_THEN_PREMIUM]: 'Gebühren vor Beiträgen'
};

/**
 * German UI labels for the components an extension point with execution type
 * "UI action" can mount. Without this translation the exit block would show the
 * raw ordinal only.
 */
const EXIT_UI_COMPONENT_LABELS: Record<BdDunningExitUiComponent, string> = {
    [BdDunningExitUiComponent.CONTRACT_DECISION_TABLE]: 'Vertragsentscheidung (Tabelle)',
    [BdDunningExitUiComponent.LV_DECISION_TABLE]: 'LV-Entscheidung (Tabelle)',
    [BdDunningExitUiComponent.HANDOVER_APPROVAL]: 'Inkasso-Übergabe: Freigabe',
    [BdDunningExitUiComponent.PLAN_BREAK_DECISION]: 'Plan-Bruch: Entscheidung',
    [BdDunningExitUiComponent.PAUSE_DIALOG]: 'Mahnpause',
    [BdDunningExitUiComponent.MANUAL_DUNNING_DIALOG]: 'Manuelle Mahnung',
    [BdDunningExitUiComponent.ESCALATION_DIALOG]: 'Eskalation',
    [BdDunningExitUiComponent.INKA_CONFIRMATION]: 'Inkasso-Bestätigung',
    [BdDunningExitUiComponent.WRITE_OFF_DIALOG]: 'Abschreibung',
    [BdDunningExitUiComponent.RATE_AGREEMENT_DIALOG]: 'Ratenvereinbarung',
    [BdDunningExitUiComponent.SPECIAL_HANDLING_DIALOG]: 'Sonderbehandlung',
    [BdDunningExitUiComponent.RESTART_DIALOG]: 'Neustart',
    [BdDunningExitUiComponent.GENERIC_FORM]: 'Generisches Formular',
    [BdDunningExitUiComponent.HANDOVER_CONFIGURATION]: 'Inkasso-Übergabe: Konfiguration'
};

/** German UI labels for the generic entity status. */
const ENTITY_STATUS_LABELS: Record<EntityStatusEnum, string> = {
    [EntityStatusEnum.draft]: 'Entwurf',
    [EntityStatusEnum.pending]: 'In Prüfung',
    [EntityStatusEnum.discarded]: 'Verworfen',
    [EntityStatusEnum.active]: 'Aktiv',
    [EntityStatusEnum.obsolete]: 'Inaktiv',
    [EntityStatusEnum.deleted]: 'Gelöscht'
};

/** A group of extension points collected under one exit point. */
export interface BdDunningTypeExitGroup {
    /** German label of the exit point (or a placeholder when it is not set). */
    label: string;
    /** The exits of this point, in load order (orderPriority). */
    items: BdDunningTypeExitDto[];
}

@Component({
    selector: 'bd-dunning-type-overview',
    templateUrl: './bd-dunning-type-overview.component.html',
    styleUrls: ['./bd-dunning-type-overview.component.scss'],
    standalone: false
})
export class BdDunningTypeOverviewComponent implements OnInit, OnChanges, OnDestroy {

    /** Id of the DunningType whose configuration is rendered. */
    @Input() dunningTypeId: number;

    /** Header DTO of the DunningType, passed down from the OBC. */
    @Input() dunningTypeDto: DunningTypeDto;

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized = false;

    /** Loading state of the structural blocks (levels, paths, transitions, ...). */
    structureLoading = false;

    /** Error state of the structural blocks. */
    structureError = false;

    /** Loading state of the deadline derivation (backend endpoint). */
    derivationLoading = false;

    /** Error state of the deadline derivation. */
    derivationError = false;

    /**
     * Reference date (what-if input): the run/dispatch date the example calculation
     * is performed with. Defaults to today (requirement 11.3). It is a pure display
     * input - nothing is persisted.
     */
    referenceDate: Date = new Date();

    // ----- Structural data -----
    levels: BdDunningTypeLevelDto[] = [];
    starts: BdDunningTypeStartDto[] = [];
    transitions: BdDunningTypeLevelTransitionDto[] = [];
    charges: BdDunningTypeChargeDto[] = [];
    stops: BdDunningTypeStopDto[] = [];
    exitGroups: BdDunningTypeExitGroup[] = [];

    // ----- Paths accordion (derived from "starts") -----

    /** The start conditions grouped per billing area (accordion panels). */
    startGroups: BdDunningTypeStartGroup[] = [];

    /**
     * Keys of the expanded accordion panels. Bound two-way to the accordion, so
     * the user's toggles write back here. It is re-initialised by every structure
     * load only - changing the reference date must not collapse the block again.
     */
    expandedStartGroupKeys: string[] = [];

    /** Summary of the paths block, e.g. "12 Pfade · 5 Abrechnungsbereiche". */
    startSummaryLabel: string | null = null;

    /** Total number of loaded extension points across all groups. */
    exitCount = 0;

    /** Actions per dunning level (requirement 7.6, grouped under the level). */
    actionsByLevelId = new Map<number, BdDunningTypeLevelActionDto[]>();

    /** Special handlings per dunning level (requirement 7.9). */
    specialHandlingsByLevelId = new Map<number, BdDunningTypeLevelSpecialHandlingDto[]>();

    /** Total number of loaded special handlings (drives the block's empty state). */
    specialHandlingCount = 0;

    // ----- Derived data (backend) -----

    /** Resolved offsets and example date chain per level, keyed by level id. */
    derivationByLevelId = new Map<number, BdDunningTypeLevelDerivationDto>();

    /** State of the bank holiday calendar for the year of the reference date. */
    calendarInfo: BdDunningTypeCalendarInfoDto | null = null;

    // ----- Header block, derived from the DTO (requirement 7.1) -----
    headerName: string | null = null;
    headerDescription: string | null = null;
    headerStatusLabel: string | null = null;
    headerMaxPauseLabel: string | null = null;
    headerPrintTemplateLabel: string | null = null;
    headerAllocationOrderLabel: string | null = null;

    private structureSub?: Subscription;
    private specialHandlingSub?: Subscription;
    private derivationSub?: Subscription;

    constructor(
        protected structureService: BdDunningTypeStructureService,
        protected dunningTypeService: DunningTypeService
    ) {
    }

    // ------------------------------------------------------------------ //
    // Lifecycle
    // ------------------------------------------------------------------ //

    ngOnInit(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['dunningTypeId']) {
            this.refreshComponent();
        } else if (changes['dunningTypeDto']) {
            this.refreshHeader();
        }
    }

    ngOnDestroy(): void {
        this.structureSub?.unsubscribe();
        this.specialHandlingSub?.unsubscribe();
        this.derivationSub?.unsubscribe();
    }

    // ------------------------------------------------------------------ //
    // Loading
    // ------------------------------------------------------------------ //

    /** Reloads header block, structural data and deadline derivation. */
    refreshComponent(): void {
        this.refreshHeader();

        if (this.dunningTypeId == null) {
            return;
        }
        this.loadStructure();
        this.loadDerivation();
    }

    /**
     * Loads all structural blocks in parallel. Special handlings do not hang off the
     * DunningType but off the levels, so they are loaded only once the level ids are
     * known (requirement 6.4, the exception).
     */
    loadStructure(): void {
        const dunningTypeId = this.dunningTypeId;

        this.structureLoading = true;
        this.structureError = false;
        this.structureSub?.unsubscribe();

        this.structureSub = forkJoin({
            levels: this.structureService.getLevels(dunningTypeId),
            starts: this.structureService.getStarts(dunningTypeId),
            transitions: this.structureService.getLevelTransitions(dunningTypeId),
            actions: this.structureService.getLevelActions(dunningTypeId),
            charges: this.structureService.getCharges(dunningTypeId),
            stops: this.structureService.getStops(dunningTypeId),
            exits: this.structureService.getExits(dunningTypeId)
        }).subscribe({
            next: result => {
                this.levels = result.levels ?? [];
                this.starts = result.starts ?? [];
                this.transitions = result.transitions ?? [];
                this.charges = result.charges ?? [];
                this.stops = result.stops ?? [];

                // The paths are grouped exactly once per load. The expand state is
                // (re-)initialised here and nowhere else: a new reference date only
                // reloads the derivation and must leave the accordion untouched.
                this.startGroups = this.groupStarts(this.starts);
                this.startSummaryLabel = this.buildStartSummaryLabel(this.startGroups);
                this.expandedStartGroupKeys = this.startGroups.length === 1
                    ? [this.startGroups[0].key]
                    : [];

                this.actionsByLevelId = this.groupActionsByLevel(result.actions ?? []);
                this.exitGroups = this.groupExits(result.exits ?? []);
                this.exitCount = (result.exits ?? []).length;

                this.structureLoading = false;
                this.loadSpecialHandlings();
            },
            error: () => {
                this.structureLoading = false;
                this.structureError = true;
            }
        });
    }

    /** Loads the special handlings for the previously loaded dunning levels. */
    loadSpecialHandlings(): void {
        const levelIds = this.levels.map(level => level.id).filter(id => id != null);

        this.specialHandlingSub?.unsubscribe();
        this.specialHandlingSub = this.structureService.getLevelSpecialHandlings(levelIds).subscribe({
            next: handlings => {
                this.specialHandlingsByLevelId = this.groupSpecialHandlingsByLevel(handlings ?? []);
                this.specialHandlingCount = (handlings ?? []).length;
            },
            error: () => {
                this.specialHandlingsByLevelId = new Map();
                this.specialHandlingCount = 0;
            }
        });
    }

    /**
     * Loads the resolved Model-C offsets including their provenance and the example
     * date chain for the current reference date. All bank day, holiday and
     * section 193 arithmetic happens in the backend.
     */
    loadDerivation(): void {
        this.derivationLoading = true;
        this.derivationError = false;
        this.derivationSub?.unsubscribe();

        this.derivationSub = this.dunningTypeService
            .getDeadlineDerivation(this.dunningTypeId, this.toIsoDate(this.referenceDate))
            .subscribe({
                next: (derivation: BdDunningTypeDeadlineDerivationDto) => {
                    this.derivationByLevelId = new Map<number, BdDunningTypeLevelDerivationDto>();
                    for (const level of derivation?.levels ?? []) {
                        this.derivationByLevelId.set(level.dunningTypeLevelId, level);
                    }
                    this.calendarInfo = derivation?.calendarInfo ?? null;
                    this.derivationLoading = false;
                },
                error: () => {
                    this.derivationByLevelId = new Map();
                    this.calendarInfo = null;
                    this.derivationLoading = false;
                    this.derivationError = true;
                }
            });
    }

    /**
     * Takes a new reference date from the date field. Deliberately wired as
     * {@code [ngModel]} + {@code (ngModelChange)} rather than the two-way binding
     * with {@code (onSelect)}: {@code onSelect} fires only on a calendar click, not
     * on a typed entry.
     */
    onReferenceDateSelected(date: Date): void {
        this.referenceDate = date;
        this.onReferenceDateChange();
    }

    /**
     * Reacts to a new reference date: only the derivation is reloaded, because the
     * structural data does not depend on the date.
     */
    onReferenceDateChange(): void {
        if (this.dunningTypeId == null || !this.referenceDate) {
            return;
        }
        this.loadDerivation();
    }

    /** Resets the reference date to today and reloads the derivation. */
    resetReferenceDate(): void {
        this.referenceDate = new Date();
        this.onReferenceDateChange();
    }

    // ------------------------------------------------------------------ //
    // Paths accordion
    // ------------------------------------------------------------------ //

    /**
     * Expands every path group. A NEW array is assigned on purpose: the
     * accordion's {@code value} is a model signal, so an in-place mutation would
     * not be picked up.
     */
    expandAllStartGroups(): void {
        this.expandedStartGroupKeys = this.startGroups.map(group => group.key);
    }

    /** Collapses every path group (see {@link expandAllStartGroups}). */
    collapseAllStartGroups(): void {
        this.expandedStartGroupKeys = [];
    }

    /** German count label of a path group ("1 Pfad" / "N Pfade"). */
    pathCountLabel(count: number): string {
        return count === 1 ? '1 Pfad' : `${count} Pfade`;
    }

    // ------------------------------------------------------------------ //
    // Template accessors
    // ------------------------------------------------------------------ //

    /** Returns the deadline derivation of a level, or {@code null}. */
    derivationFor(level: BdDunningTypeLevelDto): BdDunningTypeLevelDerivationDto | null {
        return this.derivationByLevelId.get(level.id) ?? null;
    }

    /** Returns the actions of a level (never {@code null}). */
    actionsFor(level: BdDunningTypeLevelDto): BdDunningTypeLevelActionDto[] {
        return this.actionsByLevelId.get(level.id) ?? [];
    }

    /** Returns the special handlings of a level (never {@code null}). */
    specialHandlingsFor(level: BdDunningTypeLevelDto): BdDunningTypeLevelSpecialHandlingDto[] {
        return this.specialHandlingsByLevelId.get(level.id) ?? [];
    }

    /**
     * Labels a resolved offset including its unit, derived from the offset's OWN
     * day type (calendar or bank working days) — there is no fixed 'BT'/'KT', the
     * day type is configurable per level and per value (requirement §1.3). While
     * the derivation is still missing, a placeholder is returned.
     */
    offsetValueLabel(offset: BdDunningTypeResolvedOffsetDto | null | undefined): string {
        if (!offset || offset.value == null) {
            return '—';
        }
        const unit = offset.dayType != null ? (DUNNING_DAY_COUNT_TYPE_SHORT_LABELS[offset.dayType] ?? '') : '';
        return unit ? `${offset.value} ${unit}` : `${offset.value}`;
    }

    /** German provenance label of an offset ("Konfiguriert" / "nicht konfiguriert"). */
    offsetSourceLabel(offset: BdDunningTypeResolvedOffsetDto | null | undefined): string {
        if (!offset || offset.source == null) {
            return '—';
        }
        return BD_DUNNING_OFFSET_SOURCE_LABELS[offset.source] ?? '—';
    }

    /**
     * Severity of the provenance badge: configured values are "info", values that
     * are not configured on the level (and therefore count as 0 days, business
     * rule §1.2) are "warn" - exactly this distinction is the business core of the
     * view (finding 1.3).
     */
    offsetSourceSeverity(offset: BdDunningTypeResolvedOffsetDto | null | undefined): 'info' | 'warn' {
        return offset?.source === BdDunningOffsetSource.NOT_CONFIGURED ? 'warn' : 'info';
    }

    /**
     * Spells out the deadline formula of a level in words, using the values, day
     * types and roll modes that are actually configured on the level (requirement
     * 7.3 / bd-dunning-level-daytype §1.3): the day type of each value and whether
     * each derived date is rolled to the next bank day are configurable per level.
     */
    formulaText(derivation: BdDunningTypeLevelDerivationDto | null): string {
        if (!derivation) {
            return '';
        }
        return `Versanddatum + ${this.dayCountPhrase(derivation.postalTransit?.value, derivation.postalTransit?.dayType)} Postlauf`
            + ` + ${this.dayCountPhrase(derivation.paymentTerm?.value, derivation.paymentTerm?.dayType)} Zahlfrist`
            + ` = kommunizierter Stichtag${this.rollModeClause(derivation.communicatedDeadlineRollMode)};`
            + ` + ${this.dayCountPhrase(derivation.buffer?.value, derivation.buffer?.dayType)} Puffer`
            + ` = interner Stichtag${this.rollModeClause(derivation.internalDeadlineRollMode)}, der die Eskalation auslöst.`;
    }

    /** Spells out the path A dunning start formula in words. */
    mahnstartFormulaText(derivation: BdDunningTypeLevelDerivationDto | null): string {
        if (!derivation) {
            return '';
        }
        return `Versanddatum + ${this.dayCountPhrase(derivation.gracePeriod?.value, derivation.gracePeriod?.dayType)} Karenz`
            + ` = Mahnstart (nur Pfad A)${this.rollModeClause(derivation.dunningStartRollMode)}.`;
    }

    /**
     * Renders a value + day type as one phrase, e.g. "3 Bankarbeitstage". Falls
     * back to '—' for a missing value (never the literal string "undefined")
     * and omits the unit cleanly (no double space) when the day type itself is
     * missing.
     */
    private dayCountPhrase(value: number | null | undefined, dayType: DunningDayCountType | null | undefined): string {
        const valueText = value == null ? '—' : `${value}`;
        const unit = dayType == null ? '' : (DUNNING_DAY_COUNT_TYPE_LABELS[dayType] ?? '');
        return unit ? `${valueText} ${unit}` : valueText;
    }

    /**
     * Trailing clause describing whether a derived date is rolled to the next
     * bank day, per the level's configured roll mode for that date (configurable
     * per level and per date, e.g. because dunning level 1 is only a reminder
     * and not a qualified notice). A missing roll mode is reported as unknown
     * rather than silently assumed to be NONE.
     */
    private rollModeClause(rollMode: DunningDeadlineRollMode | null | undefined): string {
        if (rollMode == null) {
            return ' (Verschiebung unbekannt)';
        }
        return rollMode === DunningDeadlineRollMode.NEXT_BANK_DAY
            ? ' (fällt dieser auf einen Samstag, Sonntag oder Feiertag, wird er auf den nächsten Bankarbeitstag verschoben)'
            : ' (wird nicht verschoben, auch wenn er auf einen Samstag, Sonntag oder Feiertag fällt)';
    }

    // ----- Enum labels -----

    /** German label of a status; {@code null} stays {@code null}. */
    entityStatusLabel(status: EntityStatusEnum | null | undefined): string | null {
        return status == null ? null : (ENTITY_STATUS_LABELS[status] ?? null);
    }

    /** {@code true} when a configuration row is not active (business rule 7). */
    isInactive(status: EntityStatusEnum | null | undefined): boolean {
        return status != null && status !== EntityStatusEnum.active;
    }

    /** German label of a payment method (bd-wide helper, dash on null). */
    paymentMethodLabel(method: PaymentMethod | null | undefined): string {
        return resolvePaymentMethodLabel(method);
    }

    /** German label of an action type. */
    actionTypeLabel(type: BdDunningActionType | null | undefined): string | null {
        return type == null ? null : (ACTION_TYPE_LABELS[type] ?? null);
    }

    /** German label of an action execution type. */
    actionExecutionTypeLabel(type: BdDunningActionExecutionType | null | undefined): string | null {
        return type == null ? null : (ACTION_EXECUTION_TYPE_LABELS[type] ?? null);
    }

    /** German label of an action timing. */
    actionTimingLabel(timing: BdDunningActionTiming | null | undefined): string | null {
        return timing == null ? null : (BD_DUNNING_ACTION_TIMING_LABELS[timing] ?? null);
    }

    /** German label of the valuta date timing of a charge action. */
    chargeValutaDateTimingLabel(timing: BdDunningChargeValutaDateTiming | null | undefined): string | null {
        return timing == null ? null : (CHARGE_VALUTA_DATE_TIMING_LABELS[timing] ?? null);
    }

    /** German label of a transition type. */
    transitionTypeLabel(type: BdDunningTransitionType | null | undefined): string | null {
        return type == null ? null : (BD_DUNNING_TRANSITION_TYPE_LABELS[type] ?? null);
    }

    /** German label of a balance check. */
    balanceCheckLabel(check: BdDunningBalanceCheck | null | undefined): string | null {
        return check == null ? null : (BD_DUNNING_BALANCE_CHECK_LABELS[check] ?? null);
    }

    /** German label of a stop condition type. */
    stopConditionTypeLabel(type: DunningStopConditionType | null | undefined): string | null {
        return type == null ? null : (DUNNING_STOP_CONDITION_TYPE_LABELS[type] ?? null);
    }

    /** German label of a balance field. */
    balanceFieldLabel(field: DunningBalanceField | null | undefined): string | null {
        return field == null ? null : (DUNNING_BALANCE_FIELD_LABELS[field] ?? null);
    }

    /**
     * The waiting period of a stop rule as one readable phrase, e.g. „5 Bankarbeitstage".
     *
     * <p>Returns {@code null} when the rule observes none — {@code NONE} already IS the
     * "switched off" state (decision K06), so there is no separate apply flag and nothing to
     * render. The template says so in its own sentence rather than printing an empty value; see
     * {@link stopHasNoWaitingPeriod}.</p>
     */
    stopWaitingPeriodLabel(stop: BdDunningTypeStopDto): string | null {
        const type = stop?.waitingPeriodType;
        if (type == null || type === BdDunningStopWaitingPeriodType.NONE) {
            return null;
        }

        const unit = BD_DUNNING_STOP_WAITING_PERIOD_TYPE_LABELS[type] ?? null;
        if (unit == null) {
            return null;
        }

        return stop.waitingPeriodDays != null ? `${stop.waitingPeriodDays} ${unit}` : unit;
    }

    /**
     * {@code true} when a stop rule ends the dunning immediately — no waiting period configured.
     *
     * An unset {@code waitingPeriodType} counts as "no waiting period": that is how the
     * evaluation reads it, and a display that stayed silent about it would leave the reader to
     * guess whether the value is missing or the behaviour is.
     */
    stopHasNoWaitingPeriod(stop: BdDunningTypeStopDto): boolean {
        return stop?.waitingPeriodType == null
            || stop.waitingPeriodType === BdDunningStopWaitingPeriodType.NONE;
    }

    /** German label of a special handling type. */
    specialHandlingTypeLabel(type: DunningSpecialHandlingType | null | undefined): string | null {
        return type == null ? null : (DUNNING_SPECIAL_HANDLING_TYPE_LABELS[type] ?? null);
    }

    /** German label of an exit execution type. */
    exitExecutionTypeLabel(type: BdDunningExitExecutionType | null | undefined): string | null {
        return type == null ? null : (BD_DUNNING_EXIT_EXECUTION_TYPE_LABELS[type] ?? null);
    }

    /**
     * Name of the artefact an extension point is bound to
     * (Logic / NotificationType / UI component / WfProcessType).
     */
    exitArtefactLabel(exit: BdDunningTypeExitDto): string | null {
        return exit.logicDtoName
            || exit.notificationTypeDtoName
            || exit.workflowProcessTypeDtoName
            || (exit.uiComponent != null ? (EXIT_UI_COMPONENT_LABELS[exit.uiComponent] ?? null) : null);
    }

    /** German plain text of an ISO-8601 period (e.g. 'P30D' -> '30 Tage'). */
    durationLabel(value: string | null | undefined): string | null {
        return formatIsoDurationDe(value);
    }

    // ------------------------------------------------------------------ //
    // Internal helpers
    // ------------------------------------------------------------------ //

    /** Takes the header data from the DunningType DTO passed down by the OBC. */
    private refreshHeader(): void {
        // The OBC passes down the generic object DTO; it carries more fields than the
        // typed DunningTypeDto class declares (maxPauseDays, printTemplate,
        // description, entityStatus). Access therefore goes through an untyped view
        // here rather than index access in the template.
        const raw = this.dunningTypeDto as unknown as Record<string, unknown> | null;

        this.headerName = (raw?.['name'] as string) ?? null;
        this.headerDescription = (raw?.['description'] as string) ?? null;
        this.headerStatusLabel = this.entityStatusLabel(raw?.['entityStatus'] as EntityStatusEnum);

        const maxPauseDays = raw?.['maxPauseDays'] as number | null | undefined;
        this.headerMaxPauseLabel = maxPauseDays == null
            ? 'unbegrenzt'
            : `${maxPauseDays} Tage`;

        this.headerPrintTemplateLabel = (raw?.['printTemplateDtoName'] as string) ?? null;

        const allocationOrder =
            raw?.['paymentDistributionAllocationOrder'] as BdDunningDistributionAllocationOrderEnum | null;
        this.headerAllocationOrderLabel = allocationOrder == null
            ? null
            : (ALLOCATION_ORDER_LABELS[allocationOrder] ?? null);
    }

    /**
     * Groups the start conditions by their billing area.
     *
     * A start without a billing area applies to every billing area (business
     * rule 4) and therefore forms the first group; the remaining groups follow
     * in German alphabetical order. Within a group the load order
     * (orderPriority asc) is kept.
     */
    private groupStarts(starts: BdDunningTypeStartDto[]): BdDunningTypeStartGroup[] {
        const groups = new Map<string, BdDunningTypeStartGroup>();

        for (const start of starts) {
            const billingAreaId = start.billingAreaDtoId ?? null;
            const key = billingAreaId == null ? START_GROUP_KEY_ALL : `ba-${billingAreaId}`;

            let group = groups.get(key);
            if (!group) {
                group = {
                    key,
                    billingAreaId,
                    label: billingAreaId == null
                        ? 'Alle Abrechnungsbereiche'
                        : (start.billingAreaDtoName || `Abrechnungsbereich ${billingAreaId}`),
                    starts: [],
                    inactiveCount: 0
                };
                groups.set(key, group);
            }

            group.starts.push(start);
            if (this.isInactive(start.entityStatus)) {
                group.inactiveCount++;
            }
        }

        return Array.from(groups.values()).sort((left, right) => {
            if (left.key === START_GROUP_KEY_ALL) {
                return -1;
            }
            if (right.key === START_GROUP_KEY_ALL) {
                return 1;
            }
            return left.label.localeCompare(right.label, 'de', {sensitivity: 'base'});
        });
    }

    /**
     * Builds the summary of the paths block ("12 Pfade · 5 Abrechnungsbereiche").
     * The "applies to all" group is not a billing area and is therefore not
     * counted.
     */
    private buildStartSummaryLabel(groups: BdDunningTypeStartGroup[]): string | null {
        const pathCount = groups.reduce((total, group) => total + group.starts.length, 0);
        if (!pathCount) {
            return null;
        }

        const billingAreaCount = groups.filter(group => group.billingAreaId != null).length;
        const parts = [this.pathCountLabel(pathCount)];
        if (billingAreaCount) {
            parts.push(billingAreaCount === 1
                ? '1 Abrechnungsbereich'
                : `${billingAreaCount} Abrechnungsbereiche`);
        }
        return parts.join(' · ');
    }

    /** Groups the actions by their dunning level. */
    private groupActionsByLevel(
        actions: BdDunningTypeLevelActionDto[]
    ): Map<number, BdDunningTypeLevelActionDto[]> {
        const grouped = new Map<number, BdDunningTypeLevelActionDto[]>();
        for (const action of actions) {
            const levelId = action.dunningTypeLevelDtoId;
            if (levelId == null) {
                continue;
            }
            const bucket = grouped.get(levelId) ?? [];
            bucket.push(action);
            grouped.set(levelId, bucket);
        }
        return grouped;
    }

    /** Groups the special handlings by their dunning level. */
    private groupSpecialHandlingsByLevel(
        handlings: BdDunningTypeLevelSpecialHandlingDto[]
    ): Map<number, BdDunningTypeLevelSpecialHandlingDto[]> {
        const grouped = new Map<number, BdDunningTypeLevelSpecialHandlingDto[]>();
        for (const handling of handlings) {
            const levelId = handling.dunningTypeLevelDtoId;
            if (levelId == null) {
                continue;
            }
            const bucket = grouped.get(levelId) ?? [];
            bucket.push(handling);
            grouped.set(levelId, bucket);
        }
        return grouped;
    }

    /**
     * Groups the extension points by their exit point, in enum declaration order
     * (dunning start ... plan breach), so the groups follow the lifecycle.
     */
    private groupExits(exits: BdDunningTypeExitDto[]): BdDunningTypeExitGroup[] {
        const buckets = new Map<number, BdDunningTypeExitDto[]>();
        const unassigned: BdDunningTypeExitDto[] = [];

        for (const exit of exits) {
            if (exit.exitPoint == null) {
                unassigned.push(exit);
                continue;
            }
            const bucket = buckets.get(exit.exitPoint) ?? [];
            bucket.push(exit);
            buckets.set(exit.exitPoint, bucket);
        }

        const groups: BdDunningTypeExitGroup[] = [];
        const orderedPoints = Object.values(BdDunningExitPoint)
            .filter(value => typeof value === 'number') as BdDunningExitPoint[];

        for (const point of orderedPoints) {
            const items = buckets.get(point);
            if (items?.length) {
                groups.push({label: BD_DUNNING_EXIT_POINT_LABELS[point] ?? String(point), items});
            }
        }
        if (unassigned.length) {
            groups.push({label: 'Ohne Exit-Punkt', items: unassigned});
        }
        return groups;
    }

    /**
     * Formats a date as an ISO date (yyyy-MM-dd) for the query parameter.
     * Deliberately not {@code toISOString()}: that converts to UTC and, depending on
     * the timezone, jumps back one day.
     */
    private toIsoDate(date: Date): string | undefined {
        if (!date) {
            return undefined;
        }
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
    }
}
